import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useMemo } from 'react';
import {
  SingularStationArea,
  StationaryArea,
  RackingArea,
  HighwayArea,
  Areas,
  ManualArea,
  LoadHandlingPoiSpec,
} from '@thive/fddm-types';

import { SELECTABLE_VEHICLE_AMOUNT_PER_AREA, UnifiedVehicle } from '@/modules/vehicles';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { getMultiSelectDisplayValue } from '@/modules/common/components/inputs';
import { assertNever } from '@/helpers/utils';
import { areaFactory } from '@/modules/floorplanDesign/factories';
import { areasToFlatCollection } from '@/modules/floorplanDesign/helpers';
import { isAtLeast1 } from '@/modules/floorplanDesign/helpers/minItems';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { getVehicleTypesByArea } from '@/modules/floorplanDesign/helpers/vehicleType';

export const useVehicleType = ({
  selectedShapes,
  enabledVehicles,
}: {
  selectedShapes: Areas;
  enabledVehicles: UnifiedVehicle[];
}): {
  displayValue: string;
  options: {
    label: string;
    value: string;
  }[];
  selectedOptions: string[];
  onChange: (clickedOptionValue: string) => void;
} => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbarStore();
  const selectedShapesRef = useRef(selectedShapes);
  const fddmSvc = FddmServiceFactory.getInstance().createFddmService();

  useEffect(() => {
    selectedShapesRef.current = selectedShapes;
  }, [selectedShapes]);

  const options = useMemo(
    () =>
      enabledVehicles?.map((item) => ({
        label: item.name,
        value: item.id,
      })),
    [enabledVehicles],
  );

  const selectedOptions: string[] = useMemo(() => {
    const areas = areasToFlatCollection(selectedShapes);

    if (areas.length !== 1) return [];

    const area = areas[0];
    return getVehicleTypesByArea(area) ?? [];
  }, [selectedShapes]);

  const displayValue = getMultiSelectDisplayValue(enabledVehicles, selectedOptions);

  const onChange = useCallback(
    (clickedOptionValue: string) => {
      const selectedShapes = selectedShapesRef.current;
      const areas = areasToFlatCollection(selectedShapes);
      if (areas.length !== 1) return;

      const area = areas[0];
      const { areaType } = area;

      const currentVehicleTypeIds = getVehicleTypesByArea(area) ?? [];
      const isSelecting = !currentVehicleTypeIds.includes(clickedOptionValue);
      if (isSelecting) {
        fddmSvc.setPreference('area.vehicleTypeId', clickedOptionValue);
      }

      switch (areaType) {
        case 'SINGULAR': {
          updateSingular(area);
          break;
        }
        case 'STATIONARY': {
          updateStationary(area);
          break;
        }
        case 'RACKING': {
          updateRacking(area);
          break;
        }
        case 'HIGHWAY': {
          updateHighway(area);
          break;
        }
        case 'MANUAL': {
          updateManual(area);
          break;
        }
        default: {
          assertNever(areaType);
        }
      }

      function updateSingular(area: SingularStationArea) {
        const isCurrentlySelected = area.loadHandlingPoiSpecs.find(
          (item) => item.vehicleTypeId === clickedOptionValue,
        );

        if (isCurrentlySelected) {
          /*
              --- Deselect
            */
          const updated: LoadHandlingPoiSpec[] = area.loadHandlingPoiSpecs.filter(
            (item) => item.vehicleTypeId !== clickedOptionValue,
          );

          if (!isAtLeast1(updated)) {
            showMinVehiclesAmountSnackbar();
            return; // exit function without updating
          }

          fddmSvc.updateArea({
            ...area,
            loadHandlingPoiSpecs: updated,
          });
        } else {
          /*
              --- Select
            */
          fddmSvc.updateArea({
            ...area,
            loadHandlingPoiSpecs: [
              ...area.loadHandlingPoiSpecs,
              areaFactory.buildLoadHandlingPoiSpecBase(
                area.name,
                area.loadHandlingType,
                clickedOptionValue,
              ),
            ],
          });
        }
      }

      function updateStationary(area: StationaryArea) {
        const isCurrentlySelected = area.stationaryPoiSpecs.find(
          (item) => item.vehicleTypeId === clickedOptionValue,
        );

        if (isCurrentlySelected) {
          /*
              --- Deselect
            */
          const updated: StationaryArea['stationaryPoiSpecs'][number][] =
            area.stationaryPoiSpecs.filter((item) => item.vehicleTypeId !== clickedOptionValue);

          if (!isAtLeast1(updated)) {
            showMinVehiclesAmountSnackbar();
            return; // exit function without updating
          }

          fddmSvc.updateArea({
            ...area,
            stationaryPoiSpecs: updated,
          });
        } else {
          /*
              --- Select
            */
          fddmSvc.updateArea({
            ...area,
            stationaryPoiSpecs: [
              ...area.stationaryPoiSpecs,
              areaFactory.buildStationaryPoiSpec(area.id, clickedOptionValue),
            ],
          });
        }
      }

      function updateRacking(area: RackingArea) {
        fddmSvc.updateArea({
          ...area,
          vehicleTypeId: clickedOptionValue,
        });
      }

      function updateHighway(area: HighwayArea) {
        const isCurrentlySelected = area.highwayPoiSpecs.find(
          (item) => item.vehicleTypeId === clickedOptionValue,
        );

        if (isCurrentlySelected) {
          /*
              --- Deselect
            */
          const updated: HighwayArea['highwayPoiSpecs'][number][] = area.highwayPoiSpecs.filter(
            (item) => item.vehicleTypeId !== clickedOptionValue,
          );

          if (!isAtLeast1(updated)) {
            showMinVehiclesAmountSnackbar();
            return;
          }

          fddmSvc.updateArea({
            ...area,
            highwayPoiSpecs: updated,
          });
        } else {
          /*
            --- Select
          */
          fddmSvc.updateArea({
            ...area,
            highwayPoiSpecs: [
              ...area.highwayPoiSpecs,
              areaFactory.buildHighwayAreaPoiSpec(area.id, clickedOptionValue),
            ],
          });
        }
      }

      function updateManual(area: ManualArea) {
        const isCurrentlySelected = area.vehicleTypeIds.find((id) => id === clickedOptionValue);

        if (isCurrentlySelected) {
          /*
              --- Deselect
            */
          const updated = area.vehicleTypeIds.filter((id) => id !== clickedOptionValue);
          if (!isAtLeast1(updated)) {
            showMinVehiclesAmountSnackbar();
            return;
          }
          fddmSvc.updateArea({
            ...area,
            vehicleTypeIds: updated,
          });
        } else {
          /*
              --- Select
            */
          fddmSvc.updateArea({
            ...area,
            vehicleTypeIds: [...area.vehicleTypeIds, clickedOptionValue],
          });
        }
      }

      function showMinVehiclesAmountSnackbar() {
        showSnackbar(
          t(
            'interface:properties.area.supported_vehicle.not_allowed_to_deselect_vehicle_below_amount',
            `Can not deselect vehicle. Minimum amount: ${SELECTABLE_VEHICLE_AMOUNT_PER_AREA.MIN}`,
            { amount: SELECTABLE_VEHICLE_AMOUNT_PER_AREA.MIN },
          ),
        );
      }
    },
    [fddmSvc, showSnackbar, t],
  );

  return { displayValue, options, selectedOptions, onChange };
};
